import { useEffect, useRef, useState } from "react";
import { useMotionValue, animate, useMotionValueEvent } from "framer-motion";

function useReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useAnimatedNumber(
  target: number,
  decimals: number,
  duration: number
): string {
  const motionValue = useMotionValue(0);
  const prevTarget = useRef(0);
  const reducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(() => (0).toFixed(decimals));

  useMotionValueEvent(motionValue, "change", (v) => {
    setDisplay(v.toFixed(decimals));
  });

  useEffect(() => {
    const from = prevTarget.current;
    prevTarget.current = target;
    const controls = animate(motionValue, target, {
      from,
      duration: reducedMotion ? 0 : duration / 1000,
      ease: "easeOut",
    });
    return () => controls.stop();
  }, [target, duration, reducedMotion]);

  return display;
}

type AnimatedMetricFormat = "currency" | "percent" | "integer" | "decimal";

interface AnimatedMetricProps {
  value: number | null | undefined;
  format: AnimatedMetricFormat;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number;
  className?: string;
}

function getDefaultDecimals(format: AnimatedMetricFormat): number {
  if (format === "integer") return 0;
  if (format === "percent") return 1;
  return 2;
}

export default function AnimatedMetric({
  value,
  format,
  prefix,
  suffix,
  decimals,
  duration = 800,
  className,
}: AnimatedMetricProps) {
  const resolvedDecimals = decimals ?? getDefaultDecimals(format);
  const isInvalid = value === null || value === undefined || isNaN(value as number);
  const absValue = isInvalid ? 0 : Math.abs(value as number);
  const isNegative = !isInvalid && (value as number) < 0;

  const displayValue = useAnimatedNumber(isInvalid ? 0 : absValue, resolvedDecimals, duration);

  if (isInvalid) {
    return <span className={className}>—</span>;
  }

  return (
    <span className={className}>
      {isNegative ? <span>-</span> : prefix ? <span>{prefix}</span> : null}
      <span>{displayValue}</span>
      {suffix && <span>{suffix}</span>}
    </span>
  );
}
